import { Intent } from "@blueprintjs/core";
import { ButtonWithDialog } from "@focus/focusUI";
import { useMemo } from "react";
import { usePhonebook } from "../../hooks/usePhonebook";
import { ROLES, useRoles } from "../../hooks/useRoles";
import type { Contact } from "../../types/phonebook";
import { useMe } from "../../hooks/useMe";

interface DeleteContactButtonProps {
  contact: Contact;
  notify?: (args: { intent: Intent; message: string }) => void;
  disabled?: boolean;
}

export const DeleteContactButton = ({
  contact,
  notify,
  disabled,
}: DeleteContactButtonProps) => {
  const me = useMe();
  const { deleteContact } = usePhonebook();
  const { roles } = useRoles();

  const canDelete = useMemo(() => {
    if (roles.includes(ROLES.ADMIN)) return true;
    if (!roles.includes(ROLES.ORG_ADMIN)) return false;

    return (
      contact?.organizations?.some((org) => me?.organizations?.includes(org)) ??
      false
    );
  }, [roles, me?.organizations, contact?.organizations]);

  if (!canDelete) return null; // Do not show button

  return (
    <ButtonWithDialog<null>
      icon="trash"
      minimal
      intent={Intent.DANGER}
      headerTitle="Confirm Delete"
      defaultContent={null}
      closeAfterAccept={true}
      onAccept={() => {
        deleteContact(contact?.id);
        notify?.({ intent: Intent.SUCCESS, message: "Contact deleted." });
      }}
      hideButtons={false}
      form={() => (
        <div className="w-full p-4 text-center space-y-3">
          <p>
            Are you sure you want to delete <b>{contact?.contactName}</b>?
          </p>
          <p className="text-sm text-gray-500">This action cannot be undone.</p>
        </div>
      )}
      disabled={disabled}
    />
  );
};
